/**
 * Life4Earth V2 — API Client
 *
 * Thin fetch wrapper for the ASP.NET Core backend.
 * All API adapter modules use this client.
 *
 * Authentication modes:
 *   1. JWT Bearer (production): Authorization header from AuthContext
 *   2. X-User-Guid (dev/fake auth): Header injection for local development
 *
 * Configuration:
 *   - Base URL from VITE_API_BASE_URL env var (or REACT_APP_API_BASE_URL)
 *   - JSON content type by default
 */

import { getAuthBridge } from './authBridge';

// DEV-1418 mobile Phase 1: base URL resolution and auth callbacks moved out
// of this file so the module graph rooted here is importable from React
// Native (Metro cannot compile import.meta; RN has no localStorage/window
// event system). Platform specifics live in:
//   - ./apiBase.ts    — base URL (web: Vite env; mobile: Metro alias swaps it)
//   - ./authBridge.ts — token access/refresh + terminal-auth callbacks
// Keep this file free of import.meta, DOM APIs, and web-only imports.
// (Existing `window.*` references below are runtime-guarded/try-caught and
// are no-ops on RN; do not add new ones.)
export { API_BASE_URL } from './apiBase';
import { API_BASE_URL } from './apiBase';

/**
 * Module-level user GUID — used in fake auth mode as fallback.
 * Set once at startup, updated by setCurrentUserGuid().
 * Default: Skip Davis (system admin / primary tester).
 */
const DEFAULT_USER_GUID = "FB60AFB9-C52A-9B28-8EB3-C0B600FD248D";

/**
 * Authenticated user GUID — set by AuthContext when a real JWT login succeeds.
 * Takes priority over the fake auth GUID.
 */
let _authenticatedUserGuid: string | null = null;

let _currentUserGuid: string = (() => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlUser = urlParams.get("user");
    if (urlUser && urlUser.trim()) return urlUser.trim().toUpperCase();
  } catch { /* ignore */ }
  return DEFAULT_USER_GUID;
})();

/**
 * Get the current user GUID.
 * In real auth mode, returns the JWT-authenticated user's GUID.
 * In fake auth mode, returns the dev GUID (from URL param or default).
 */
export function getCurrentUserGuid(): string {
  return _authenticatedUserGuid || _currentUserGuid;
}

/**
 * Set the authenticated user GUID (called by AuthContext on login).
 * Pass null on logout to revert to fake-auth fallback.
 */
export function setAuthenticatedUserGuid(guid: string | null): void {
  _authenticatedUserGuid = guid ? guid.toUpperCase() : null;
}

/**
 * Update the current user GUID (called by App.tsx when the gear-menu selector changes).
 * Also updates the browser URL so the identity survives refresh.
 */
export function setCurrentUserGuid(guid: string): void {
  _currentUserGuid = guid || DEFAULT_USER_GUID;

  // Update URL parameter so identity persists across refresh — no localStorage needed.
  try {
    const url = new URL(window.location.href);
    if (guid) {
      url.searchParams.set("user", guid);
    } else {
      url.searchParams.delete("user");
    }
    window.history.replaceState(null, "", url.toString());
  } catch { /* ignore */ }
}

/**
 * Core fetch wrapper. Handles:
 *   - Base URL prefixing
 *   - JSON content type
 *   - Authentication header injection (Bearer JWT or X-User-Guid)
 *   - Error response parsing
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;

  function buildHeaders(): Record<string, string> {
    const h: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };
    // Auth header: send Bearer JWT when available. No production fallback.
    //
    // Audit F3 / DEV-725: the prior unconditional X-User-Guid fallback was
    // a dev convenience that became a complete auth bypass if FakeAuth
    // ever leaked into staging/prod (FakeAuthMiddleware reads the header
    // and trusts whatever GUID it sees). We now ONLY send X-User-Guid
    // when window.__L4E_FAKE_AUTH is explicitly set — a flag that exists
    // only in local dev builds. Production code paths without a JWT fail
    // closed: the server returns 401, the global 401 handler below
    // dispatches l4e:auth-required, and SessionGate bounces the user to
    // /login.
    const t = getAuthBridge().getAccessToken();
    if (t) {
      h["Authorization"] = `Bearer ${t}`;
    } else if (typeof window !== "undefined" && (window as any).__L4E_FAKE_AUTH) {
      h["X-User-Guid"] = getCurrentUserGuid();
    }
    return h;
  }

  let response = await fetch(url, {
    ...options,
    headers: buildHeaders(),
  });

  // 401 with no token or expired token — try a transparent refresh and retry once.
  if (response.status === 401) {
    const newToken = await getAuthBridge().tryRefreshToken();
    if (newToken) {
      response = await fetch(url, {
        ...options,
        headers: buildHeaders(), // picks up the new access token
      });
    } else {
      // Terminal auth failure: refresh is gone, invalid, or expired and
      // the server rejected the request. Notify the AuthContext so the
      // session state is wiped (access token, refresh token, queryCache,
      // user) and SessionGate re-renders into the /login redirect path.
      //
      // Audit F4. The event is fired here rather than inside AuthContext
      // because apiFetch is the single chokepoint for protected calls —
      // the /api/auth/* endpoints bypass apiFetch entirely and have
      // their own 401 semantics (e.g. wrong password is NOT a session
      // wipe). We still throw via the !response.ok branch below so the
      // calling component's promise rejects.
      // Platform bridge: web dispatches 'l4e:auth-required'; mobile emits on
      // its own emitter (DEV-1418 — no window events on RN).
      getAuthBridge().onAuthRequired?.();
    }
  }

  // DEV-147: Detect account suspension before general error handling
  if (response.status === 403) {
    try {
      const body = await response.clone().json();
      if (body?.code === 'ACCOUNT_SUSPENDED') {
        // Platform bridge: web dispatches the custom event the suspension
        // overlay listens for; mobile handles it on its own emitter.
        getAuthBridge().onAccountSuspended?.(body);
        throw new ApiError(403, body.message || 'Account suspended', body);
      }
    } catch (e) {
      if (e instanceof ApiError) throw e;
      // Not a suspension response — fall through to normal error handling
    }
  }

  if (!response.ok) {
    let errorBody: any;
    try {
      const text = await response.text();
      try {
        errorBody = JSON.parse(text);
      } catch {
        // Response wasn't JSON — include raw text for debugging
        errorBody = { message: text || response.statusText, _raw: true };
      }
    } catch {
      errorBody = { message: response.statusText };
    }
    // Build a human-readable message. ASP.NET validation responses include
    // { title: "One or more validation errors occurred.", errors: { field: ["msg"] } }.
    // Extract field-level details so the user sees what actually went wrong.
    let msg = errorBody?.detail ?? errorBody?.title ?? errorBody?.message ?? errorBody?.error ?? "API request failed";
    if (errorBody?.errors && typeof errorBody.errors === 'object') {
      const fieldErrors: string[] = [];
      for (const [field, messages] of Object.entries(errorBody.errors)) {
        const msgs = Array.isArray(messages) ? messages.join('; ') : String(messages);
        fieldErrors.push(`${field}: ${msgs}`);
      }
      if (fieldErrors.length > 0) {
        msg = `${msg} — ${fieldErrors.join(' | ')}`;
      }
    }
    throw new ApiError(response.status, msg, errorBody);
  }

  // 204 No Content — return undefined
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Typed API error with status code and server message.
 */
export class ApiError extends Error {
  /** Seconds to wait before retrying (from 429 Retry-After header) */
  public readonly retryAfterSeconds?: number;

  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: any
  ) {
    super(message);
    this.name = "ApiError";

    // Parse Retry-After from 429 responses
    if (status === 429 && body?.retryAfterSeconds) {
      this.retryAfterSeconds = body.retryAfterSeconds;
    }
  }

  /** True if this error is a rate-limit rejection (HTTP 429). */
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}
